import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'

/**
 * Redirects a page on the client side. Ideally we don't ever link to this
 * route, but this should kick in and redirect the client if it's following an
 * internal link. Accessing the link directly will be redirected by
 * getStaticProps
 */
const Redirect: React.FC<{ destination: string }> = ({ destination }) => {
  const { push } = useRouter()
  useEffect(() => {
    push(destination)
  })
  return null
}

/**
 * Get a `<Redirect />` component and `getStaticProps` handler that redirects to
 * the provided destination.
 *
 * @example
 *   // in `/pages/path-to-redirect.ts`
 *   const { Redirect, getStaticProps } = getRedirect(`/my-destination`, true)
 *   export { getStaticProps }
 *   export default Redirect
 */
export function getRedirect(
  destination: string,
  permanent = false,
): { Redirect: React.ComponentType; getServerSideProps: GetServerSideProps } {
  // Component to render if arriving at this path from an internal link
  const Component = () => <Redirect destination={destination} />
  // Handler if accessing this route from an external destination, or typing in the URL bar
  const getServerSideProps: GetServerSideProps = async () => {
    return {
      redirect: {
        destination,
        permanent,
      },
    }
  }
  return {
    Redirect: Component,
    getServerSideProps,
  }
}

export default Redirect
